<template>
    <div>

        <!-- One figure, not the PO Manager's three buckets: every row here is at the same
             point of the same escalation. The oldest date is what carries information —
             it says how far behind the team is, which a count alone cannot. -->
        <div v-if="summary" class="alert alert-light border d-flex flex-wrap gap-3 mb-3">
            <span :class="{ 'text-danger fw-bold': summary.awaiting_decision > 0 }">
                <strong>{{ summary.awaiting_decision }}</strong>
                awaiting an invoicing decision
            </span>
            <span v-if="summary.oldest_final_request" class="text-muted ms-auto">
                Longest outstanding since
                {{ formatDate(summary.oldest_final_request, 'd LLL yyyy, HH:mm') }}
            </span>
        </div>

        <div>
            <table id="datatable-invoicing-list" ref="invoicingTable" class="table table-bordered table-striped table-vcenter dataTable" style="width: 100%;">
                <thead>
                    <tr>
                        <!-- Every row here is actionable, so unlike the PO Manager every row
                             is selectable and select-all has nothing to skip. -->
                        <th class="text-center" style="width: 50px;">
                            <input type="checkbox" id="invoicing-check-all" title="Select every job on this page" />
                        </th>
                        <th>Job Ref.</th>
                        <th>Customer</th>
                        <th>Delivery Date</th>
                        <th>Journey</th>
                        <th>PO Final Request Date</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody></tbody>
            </table>
        </div>

    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { bindSelectAll } from '@/helpers/bulk-select';
import { useMovementFormatting } from '@/composables/useMovementFormatting';
import { usePoActions } from '@/composables/usePoActions';
import { usePoReference } from '@/composables/usePoReference';

const props = defineProps({
    ajaxUrl: {
        type: String,
        required: true,
    },
});

const columns = [
    {
        data: 'id',
        orderable: false,
        searchable: false,
        width: '50px',
        render: (data, type, row) => row.computed_select,
    },
    {
        data: 'id',
        render: (data, type, row) => row.computed_reference,
    },
    {
        data: 'id',
        render: (data, type, row) => row.computed_customer,
    },
    {
        data: 'id',
        width: '160px',
        render: (data, type, row) => row.computed_delivery_date,
    },
    {
        data: 'id',
        width: '220px',
        render: (data, type, row) => row.computed_journey,
    },
    {
        data: 'id',
        width: '170px',
        render: (data, type, row) => row.computed_final_request,
    },
    {
        data: 'id',
        width: '260px',
        render: (data, type, row) => row.computed_action,
    },
];

const invoicingTable = ref(null);
const summary = ref(null);
const { formatDate } = useMovementFormatting();

// Shared with the PO Manager: both screens offer these two actions against the
// same two endpoints, so the confirmation wording lives in one place rather
// than being paraphrased per screen.
const { confirmThenProcess } = usePoActions();
const { confirmThenSetReference } = usePoReference();

onMounted(() => {

    const table = window.jQuery(invoicingTable.value).DataTable({
        processing: true,
        serverSide: true,
        pageLength: 25,
        lengthMenu: [25, 50, 100],
        ajax: {
            url: props.ajaxUrl,
        },
        // The order is the screen: longest-overdue first. Sorting is off rather
        // than left to the headers because the server owns that order — a header
        // click would append to it and appear to do nothing.
        ordering: false,
        order: [],
        columns,
    });

    table.on('xhr.dt', (e, settings, json) => {
        summary.value = json?.summary ?? null;
    });

    // Per page, and only over the rows currently drawn: the table is server-side,
    // so anything on another page is not in the DOM and could not be submitted.
    bindSelectAll({
        tableSelector: '#datatable-invoicing-list',
        masterSelector: '#invoicing-check-all',
    });

    // Delegated, on the table body: DataTables rebuilds every row on every draw,
    // so a handler bound to the buttons themselves would survive exactly one page.
    window.jQuery(table.table().body()).on('click', '.po-action-button', function () {
        const payload = JSON.parse(this.getAttribute('data-po-action'));

        confirmThenProcess(payload, () => table.ajax.reload(null, false));
    });

    window.jQuery(table.table().body()).on('click', '.po-set-reference-button', function () {
        const payload = JSON.parse(this.getAttribute('data-po-action'));

        confirmThenSetReference(payload, () => table.ajax.reload(null, false));
    });
});
</script>
